import ActionConfirmationDialog from '../../../components/ActionConfirmationDialog';
import { Box, Center, ProgressCircle, Table } from '@chakra-ui/react';
import { buildSubjectColumnStyles, getSubjectColumnClassName } from './teacherUtils';
import { NewTeacherRow } from './NewTeacherRow';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { TeacherTableRow } from './TeacherTableRow';
import { useEffect, useMemo, useState } from 'react';
import { useOffer } from '../../../hooks/useOffer';
import { useSlots } from '../../../hooks/useSlots';
import { useTeachers, useTeachersCreate, useTeachersDelete, useTeachersSubjectsUpdate, useTeachersUpdate } from '../../../hooks/useTeachers';

const TeachersPage = () => {
	useEffect(setDocumentTitle('Korepetytorzy')); // eslint-disable-line react-hooks/exhaustive-deps

	const [teachers, teachersLoading, teachersError] = useTeachers();
	const createTeacher = useTeachersCreate();
	const deleteTeacher = useTeachersDelete();
	const updateTeacherSubjects = useTeachersSubjectsUpdate();
	const renameTeacher = useTeachersUpdate();
	const [offer, offerLoading, offerError] = useOffer();
	const [slots, slotsLoading, slotsError] = useSlots();
	const [pendingSubjectUpdate, setPendingSubjectUpdate] = useState<string | null>(null);
	const [pendingTeacherDelete, setPendingTeacherDelete] = useState<string | null>(null);
	const [pendingTeacherCreate, setPendingTeacherCreate] = useState(false);
	const [pendingTeacherRename, setPendingTeacherRename] = useState<string | null>(null);
	const [teacherDeleteToConfirm, setTeacherDeleteToConfirm] = useState<string | null>(null);
	const [newTeacherName, setNewTeacherName] = useState('');
	const [editingTeacherName, setEditingTeacherName] = useState<string | null>(null);
	const [editingTeacherValue, setEditingTeacherValue] = useState('');

	const teacherSlotCounts = useMemo(() => {
		const counts = new Map<string, number>();
		if (!slots) {
			return counts;
		}

		Object.values(slots).forEach((slot: any) => {
			const teacherName = slot?.defaultTeacher;
			if (!teacherName) {
				return;
			}
			counts.set(teacherName, (counts.get(teacherName) ?? 0) + 1);
		});

		return counts;
	}, [slots]);

	const subjectColumns = useMemo(() => {
		if (!offer) {
			return [] as string[];
		}

		return Object.values(offer)
			.map((item) => item?.code)
			.filter((code): code is string => !!code)
			.sort((left, right) => left.localeCompare(right, 'pl', { sensitivity: 'base' }));
	}, [offer]);

	const sortedTeachers = useMemo(() => {
		if (!teachers) {
			return [];
		}

		return Object.entries(teachers)
			.map(([name, teacher]) => ({ ...(teacher ?? {}), name }))
			.sort((a: any, b: any) => {
				return String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'pl', { sensitivity: 'base' });
			});
	}, [teachers]);

	const subjectColumnStyles = useMemo(() => buildSubjectColumnStyles(subjectColumns), [subjectColumns]);

	const handleSubjectToggle = async (teacherName: string, code: string, hasSubject: boolean) => {
		if (!teacherName || pendingSubjectUpdate) {
			return;
		}

		const teacher = teachers?.[teacherName];
		const currentSubjects = Array.isArray(teacher?.subjects) ? teacher.subjects : [];
		const nextSubjects = hasSubject
			? currentSubjects.filter((subject) => subject !== code)
			: [...new Set([...currentSubjects, code])];

		const updateKey = `${teacherName}::${code}`;
		setPendingSubjectUpdate(updateKey);
		try {
			await updateTeacherSubjects(teacherName, nextSubjects);
		} finally {
			setPendingSubjectUpdate((current) => (current === updateKey ? null : current));
		}
	};

	const handleTeacherDeleteConfirmed = async (teacherName: string) => {
		if (!teacherName || pendingTeacherDelete || pendingSubjectUpdate || pendingTeacherRename || pendingTeacherCreate) {
			return;
		}

		setPendingTeacherDelete(teacherName);
		try {
			await deleteTeacher(teacherName);
		} finally {
			setPendingTeacherDelete((current) => (current === teacherName ? null : current));
		}
	};

	const handleTeacherDelete = (teacherName: string) => {
		if (!teacherName || pendingTeacherDelete || pendingSubjectUpdate || pendingTeacherRename || pendingTeacherCreate) {
			return;
		}

		if ((teacherSlotCounts.get(teacherName) ?? 0) > 0) {
			setTeacherDeleteToConfirm(teacherName);
			return;
		}

		handleTeacherDeleteConfirmed(teacherName);
	};

	const handleTeacherCreate = async () => {
		const normalizedTeacherName = newTeacherName.trim();

		if (!normalizedTeacherName || pendingTeacherCreate || pendingTeacherDelete || pendingSubjectUpdate || pendingTeacherRename) {
			return;
		}

		setPendingTeacherCreate(true);
		try {
			await createTeacher(normalizedTeacherName);
			setNewTeacherName('');
		} finally {
			setPendingTeacherCreate(false);
		}
	};

	const handleTeacherRenameStart = (teacherName: string) => {
		if (!teacherName || pendingTeacherRename || pendingTeacherCreate || pendingTeacherDelete || pendingSubjectUpdate) {
			return;
		}

		setEditingTeacherName(teacherName);
		setEditingTeacherValue(teacherName);
	};

	const handleTeacherRenameSubmit = async (teacherName: string, teacher: any) => {
		const normalizedTeacherName = editingTeacherValue.trim();

		if (
			!teacherName ||
			!normalizedTeacherName ||
			pendingTeacherRename ||
			pendingTeacherCreate ||
			pendingTeacherDelete ||
			pendingSubjectUpdate ||
			normalizedTeacherName === teacherName
		) {
			return;
		}

		setPendingTeacherRename(teacherName);
		try {
			await renameTeacher(teacherName, normalizedTeacherName, teacher ?? { name: teacherName, subjects: [] });
			setEditingTeacherName(null);
			setEditingTeacherValue('');
		} finally {
			setPendingTeacherRename((current) => (current === teacherName ? null : current));
		}
	};

	if (teachersLoading || offerLoading || slotsLoading) {
		return (
			<Center my={12}>
				<ProgressCircle.Root value={null}>
					<ProgressCircle.Circle css={{ '--thickness': '12px' }}>
						<ProgressCircle.Track />
						<ProgressCircle.Range stroke="orange.400" />
					</ProgressCircle.Circle>
				</ProgressCircle.Root>
			</Center>
		);
	}

	if (teachersError || offerError || slotsError) {
		console.error(teachersError ?? offerError ?? slotsError);
		return null;
	}

	return (
		<>
			<style>
				{subjectColumnStyles}
				{`
					.teacher-row .teacher-row-delete {
						width: 24px;
						min-width: 24px;
						opacity: 0;
						visibility: hidden;
						pointer-events: none;
						transition: opacity 0.12s ease, visibility 0.12s ease;
					}
					.teacher-row:hover .teacher-row-delete,
					.teacher-row:focus-within .teacher-row-delete,
					.teacher-row:has(.teacher-row-delete:focus-visible) .teacher-row-delete {
						opacity: 1;
						visibility: visible;
						pointer-events: auto;
						transition-delay: 0.2s;
					}
				`}
			</style>
			<Table.ScrollArea>
				<Table.Root className="teachers-subject-matrix" size="sm" interactive showColumnBorder>
					<Table.Header>
						<Table.Row>
							<Table.ColumnHeader fontWeight="bold" pl={8} verticalAlign="bottom">Korepetytor</Table.ColumnHeader>
							<Table.ColumnHeader
								fontWeight="bold"
								fontSize="sm"
								textTransform="none"
								whiteSpace="nowrap"
								px={2}
							>
								<Box display="flex" alignItems="center" justifyContent="center" h="100%" w="100%">
									<span
										style={{
											writingMode: 'vertical-rl',
											transform: 'rotate(180deg)',
											display: 'inline-block',
											lineHeight: 1,
											minHeight: '120px',
											textAlign: 'center',
										}}
									>
										Liczba okienek
									</span>
								</Box>
							</Table.ColumnHeader>
							{subjectColumns.map((code) => (
								<Table.ColumnHeader
									key={code}
									className={getSubjectColumnClassName(code)}
									fontWeight="bold"
									fontSize="sm"
									textTransform="none"
									whiteSpace="nowrap"
									px={2}
								>
									<span
										style={{
											writingMode: 'vertical-rl',
											transform: 'rotate(180deg)',
											display: 'inline-block',
											lineHeight: 1,
											minHeight: '120px',
											textAlign: 'center',
										}}
									>
										{code}
									</span>
								</Table.ColumnHeader>
							))}
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{sortedTeachers.map((teacher: any, index) => (
							<TeacherTableRow
								key={teacher?.name || index}
								teacher={teacher}
								index={index}
								subjectColumns={subjectColumns}
								teacherSlotCounts={teacherSlotCounts}
								pendingSubjectUpdate={pendingSubjectUpdate}
								pendingTeacherDelete={pendingTeacherDelete}
								pendingTeacherCreate={pendingTeacherCreate}
								pendingTeacherRename={pendingTeacherRename}
								editingTeacherName={editingTeacherName}
								editingTeacherValue={editingTeacherValue}
								setEditingTeacherValue={setEditingTeacherValue}
								handleSubjectToggle={handleSubjectToggle}
								handleTeacherDelete={handleTeacherDelete}
								handleTeacherRenameStart={handleTeacherRenameStart}
								handleTeacherRenameSubmit={handleTeacherRenameSubmit}
								onTeacherRenameCancel={() => {
									setEditingTeacherName(null);
									setEditingTeacherValue('');
								}}
							/>
						))}
						<NewTeacherRow
							newTeacherName={newTeacherName}
							setNewTeacherName={setNewTeacherName}
							pendingTeacherCreate={pendingTeacherCreate}
							pendingTeacherDelete={pendingTeacherDelete}
							pendingTeacherRename={pendingTeacherRename}
							pendingSubjectUpdate={pendingSubjectUpdate}
							subjectColumns={subjectColumns}
							handleTeacherCreate={handleTeacherCreate}
						/>
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>
			<ActionConfirmationDialog
				isOpen={!!teacherDeleteToConfirm}
				onClose={() => setTeacherDeleteToConfirm(null)}
				onAction={() => {
					if (!teacherDeleteToConfirm) {
						return;
					}
					handleTeacherDeleteConfirmed(teacherDeleteToConfirm);
					setTeacherDeleteToConfirm(null);
				}}
				isPending={!!pendingTeacherDelete}
				title="Czy napewno chcesz usunąć korepetytora z listy?"
				bodyText={
					teacherDeleteToConfirm
						? `${teacherDeleteToConfirm} ma wpisane w systemie terminy. Ta operacja usunie powiązane terminy, czy na pewno chcesz to zrobić?`
						: 'Czy na pewno chcesz usunąć tego korepetytora?'
				}
				actionText="Usuń"
				actionColor="red"
			/>
		</>
	);
};

export default TeachersPage;
